import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box, IconButton, Typography, styled } from '@mui/material';
import { CloseOutlined, FileUploadOutlined } from '@mui/icons-material';

// A genuinely hidden native file input. The plain `hidden` attribute is only
// UA-stylesheet `display:none`, which Bootstrap's `input[type="file"] { display:
// block }` (loaded from the CDN) overrides — so `<input hidden type="file">`
// leaks the native "Choose file / No file chosen" control on top of our label.
// The clip technique below doesn't rely on `display`, so it survives that, while
// keeping the picker accessible (still focusable/tabbable).
export const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const Dropzone = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${(props) => props.theme.spacing(0.5)};
    width: 100%;
    padding: ${(props) => props.theme.spacing(3, 2)};
    border: 2px dashed ${(props) => props.theme.palette.secondary[300]};
    border-radius: 10px;
    background-color: ${(props) => props.theme.palette.common.white};
    color: ${(props) => props.theme.palette.secondary.main};
    cursor: pointer;
    text-align: center;
    transition:
        border-color 0.15s ease,
        background-color 0.15s ease;

    &:hover {
        border-color: ${(props) => props.theme.palette.primary.main};
        background-color: rgba(90, 63, 245, 0.03);
    }
`;

const SelectedFileRow = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${(props) => props.theme.spacing(1)};
    width: 100%;
    padding: ${(props) => props.theme.spacing(1.5, 2)};
    border: 1px solid ${(props) => props.theme.palette.secondary[300]};
    border-radius: 10px;
    background-color: ${(props) => props.theme.palette.common.white};
`;

export const dataURLToBlob = (dataURL) => {
    const BASE64_MARKER = ';base64,';
    if (dataURL.indexOf(BASE64_MARKER) === -1) {
        const parts = dataURL.split(',');
        const contentType = parts[0].split(':')[1];
        const raw = parts[1];

        return new Blob([raw], { type: contentType });
    }

    const parts = dataURL.split(BASE64_MARKER);
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
};

async function processFile(file, maxWidth, maxHeight) {
    if (!file) {
        throw new Error('No file provided');
    }
    // Helper function to read file as Data URL
    const readFileAsDataURL = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Helper function to load image
    const loadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    };

    // Check the file type
    const fileType = file.type;

    if (fileType === 'application/pdf') {
        // If the file is a PDF, simply read it as a Data URL
        return await readFileAsDataURL(file);
    } else if (fileType.startsWith('image/')) {
        // If the file is an image, resize it and convert to Base64
        const dataURL = await readFileAsDataURL(file);
        const img = await loadImage(dataURL);

        // Create a canvas element
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate the new dimensions
        let width = img.width;
        let height = img.height;

        if (width > height) {
            if (width > maxWidth) {
                height = Math.round((height *= maxWidth / width));
                width = maxWidth;
            }
        } else {
            if (height > maxHeight) {
                width = Math.round((width *= maxHeight / height));
                height = maxHeight;
            }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw the image onto the canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert the canvas to a Base64 encoded string
        return canvas.toDataURL('image/jpeg');
    } else {
        throw new Error('Unsupported file type');
    }
}

const FileUpload = ({
    onFileChange,
    defaultValue,
    error,
    maxSizeMb = 3,
    accept = '.jpg, .jpeg, .png, .pdf',
}) => {
    const { formatMessage } = useIntl();
    const canvasRef = useRef(null);
    const [fileUploaded, setFileUploaded] = useState(defaultValue);
    const [uploadError, setUploadError] = useState('');

    const acceptedTypes = accept
        .split(',')
        .map((type) => type.trim().replace(/^\./, ''))
        .filter(Boolean)
        .join(', ');

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        const maxWidth = 1024;
        const maxHeight = 1024;

        // allow re-selecting the same file after a rejection
        e.target.value = '';

        if (!file) {
            return;
        }

        if (file.size > maxSizeMb * 1024 * 1024) {
            setUploadError(
                formatMessage(
                    { id: 'Forms.shared.fileUpload.fileTooLarge' },
                    { maxSize: maxSizeMb }
                )
            );

            return;
        }

        try {
            const base64 = await processFile(file, maxWidth, maxHeight);

            const fileWithPreview = {
                file,
                name: file.name,
                size: file.size,
                type: file.type,
                upload_id: `${file.size}-${Date.now()}`,
                preview: base64,
            };

            setUploadError('');
            onFileChange && onFileChange(fileWithPreview);
            setFileUploaded(fileWithPreview);
        } catch (error) {
            console.error(error);
            setUploadError(formatMessage({ id: 'Forms.shared.fileUpload.processingError' }));
        }
    };

    const deleteUploadedFile = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setUploadError('');
        setFileUploaded('');
        onFileChange && onFileChange(null);
    };

    const hasFile = Boolean(fileUploaded?.preview);

    return (
        <Box display="flex" flexDirection="column" sx={{ '& > * + *': { marginTop: 1 } }}>
            {hasFile ? (
                <SelectedFileRow>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <FileUploadOutlined fontSize="small" color="primary" />
                        <Typography
                            variant="body1"
                            sx={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                            }}
                        >
                            {fileUploaded.name}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label={formatMessage({
                            id: 'Forms.shared.fileUpload.deleteFile',
                        })}
                        size="small"
                        edge="end"
                        onClick={deleteUploadedFile}
                    >
                        <CloseOutlined fontSize="small" />
                    </IconButton>
                </SelectedFileRow>
            ) : (
                <Dropzone component="label">
                    <FileUploadOutlined color="primary" />
                    <Typography variant="body1" fontWeight={500}>
                        {formatMessage({ id: 'Forms.shared.fileUpload.chooseFile' })}
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight={300}
                        fontSize={12}
                        color="text.secondary"
                    >
                        {formatMessage(
                            { id: 'Forms.shared.fileUpload.acceptedTypes' },
                            { types: acceptedTypes, maxSize: maxSizeMb }
                        )}
                    </Typography>
                    <VisuallyHiddenInput
                        type="file"
                        accept={accept}
                        id="contained-button-file"
                        onChange={handleFileChange}
                    />
                </Dropzone>
            )}

            {(uploadError || error) && (
                <Typography fontSize={12} color="error">
                    {uploadError || error}
                </Typography>
            )}

            <canvas id="canvas-" ref={canvasRef} style={{ display: 'none' }} />
            {hasFile && fileUploaded?.type !== 'application/pdf' && (
                <img
                    style={{ marginTop: '20px', marginBottom: '10px', borderRadius: '8px' }}
                    src={fileUploaded?.preview}
                    width="100%"
                    alt={fileUploaded?.name}
                />
            )}
        </Box>
    );
};

export default FileUpload;
